import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';
import { getRequestAuthToken } from '../auth/appliedAuthToken';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

const languages: Language[] = [
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
];

const PLACEMENT_TOP = 'top' as const;
const PLACEMENT_BOTTOM = 'bottom' as const;
const MENU_TOP_CLASSES = 'bottom-full mb-2';
const MENU_BOTTOM_CLASSES = 'top-full mt-2';

type LanguageMenuPlacement = typeof PLACEMENT_TOP | typeof PLACEMENT_BOTTOM;

interface LanguageSwitcherProps {
  className?: string;
  compact?: boolean;
  placement?: LanguageMenuPlacement;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  className = '',
  compact = false,
  placement,
}) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];
  const resolvedPlacement = placement ?? (compact ? PLACEMENT_BOTTOM : PLACEMENT_TOP);
  const menuPlacementClasses = resolvedPlacement === PLACEMENT_TOP ? MENU_TOP_CLASSES : MENU_BOTTOM_CLASSES;

  const handleLanguageChange = async (languageCode: string) => {
    try {
      await i18n.changeLanguage(languageCode);
      
      // Set RTL for Arabic
      if (languageCode === 'ar') {
        document.documentElement.dir = 'rtl';
        document.documentElement.lang = 'ar';
      } else {
        document.documentElement.dir = 'ltr';
        document.documentElement.lang = languageCode;
      }

      // Update user preference via API if authenticated. The guarded request
      // token fails closed during StorageEvent lag, so this persistence is
      // silently skipped rather than sending an unadopted shared bearer.
      try {
        const token = getRequestAuthToken();
        if (token) {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/locale`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ preferred_locale: languageCode }),
          });

          if (response.ok) {
            console.log('Language preference saved to user profile');
          }
        }
      } catch (error) {
        console.warn('Failed to save language preference to server:', error);
      }

      setIsOpen(false);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  if (compact) {
    return (
      <div className={`relative ${className}`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 px-2 py-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          data-testid="language-switcher-compact"
          aria-expanded={isOpen}
        >
          <Globe className="w-4 h-4" />
          <span className="text-lg">{currentLanguage.flag}</span>
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <div
              className={`absolute ${menuPlacementClasses} right-0 max-h-[60vh] w-40 overflow-y-auto overscroll-contain bg-[var(--surface)] border border-[var(--border-light)] rounded-lg shadow-lg z-20`}
              data-testid="language-menu"
            >
              {languages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => handleLanguageChange(language.code)}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-[var(--gray-50)] transition-colors first:rounded-t-lg last:rounded-b-lg flex items-center space-x-2 ${
                    language.code === i18n.language ? 'bg-[var(--color-primary-50)] text-[var(--color-primary)]' : 'text-[var(--text-primary)]'
                  }`}
                  data-testid={`language-option-${language.code}`}
                >
                  <span className="text-base">{language.flag}</span>
                  <span>{language.nativeName}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-[var(--surface)] border border-[var(--border-light)] rounded-lg hover:bg-[var(--gray-50)] transition-colors"
        data-testid="language-switcher"
        aria-expanded={isOpen}
      >
        <Globe className="w-4 h-4 text-[var(--color-primary)]" />
        <span className="text-lg">{currentLanguage.flag}</span>
        <span className="font-medium text-[var(--text-primary)]">{currentLanguage.nativeName}</span>
        <ChevronDown className={`w-4 h-4 text-[var(--text-secondary)] transition-transform ${isOpen && resolvedPlacement === PLACEMENT_TOP ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div
            className={`absolute ${menuPlacementClasses} left-0 max-h-[60vh] w-full min-w-48 overflow-y-auto overscroll-contain bg-[var(--surface)] border border-[var(--border-light)] rounded-lg shadow-lg z-20`}
            data-testid="language-menu"
          >
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-[var(--gray-50)] transition-colors first:rounded-t-lg last:rounded-b-lg flex items-center space-x-3 ${
                  language.code === i18n.language ? 'bg-[var(--color-primary-50)] text-[var(--color-primary)]' : 'text-[var(--text-primary)]'
                }`}
                data-testid={`language-option-${language.code}`}
              >
                <span className="text-base">{language.flag}</span>
                <div>
                  <div className="font-medium">{language.nativeName}</div>
                  <div className="text-xs text-[var(--text-secondary)]">{language.name}</div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSwitcher;
